import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { isWorkspaceHost } from "@/lib/subdomain";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
const MarketingHome = lazy(() => import("./pages/MarketingHome"));
import AppLayout from "./components/app/AppLayout";
import RedirectHandler from "./components/app/RedirectHandler";
import SEOHead from "./components/app/SEOHead";
import PostHogPageView from "./components/PostHogPageView";

// Lazy-loaded routes for code splitting
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PublicLanding = lazy(() => import("./pages/public/PublicLanding"));
const PublicThanks = lazy(() => import("./pages/public/PublicThanks"));
const SubdomainHome = lazy(() => import("./pages/public/SubdomainHome"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/app/Dashboard"));
const Landings = lazy(() => import("./pages/app/Landings"));
const LandingEditor = lazy(() => import("./pages/app/LandingEditor"));
const LandingStats = lazy(() => import("./pages/app/LandingStats"));
const UtmGenerator = lazy(() => import("./pages/app/UtmGenerator"));
const Integrations = lazy(() => import("./pages/app/Integrations"));
const Templates = lazy(() => import("./pages/app/Templates"));
const TemplateEditor = lazy(() => import("./pages/app/TemplateEditor"));
const AppSettings = lazy(() => import("./pages/app/Settings"));
const Account = lazy(() => import("./pages/app/Account"));
const Admin = lazy(() => import("./pages/app/Admin"));
const AdminSEO = lazy(() => import("./pages/app/AdminSEO"));
const Brand = lazy(() => import("./pages/app/Brand"));
const LinkInBio = lazy(() => import("./pages/app/LinkInBio"));
const Leads = lazy(() => import("./pages/app/Leads"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const isWorkspace = isWorkspaceHost();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <RedirectHandler />
            <SEOHead />
            <PostHogPageView />
            {isWorkspace ? (
              <Routes>
                <Route path="/" element={<SubdomainHome />} />
                <Route path="/l/:slug" element={<PublicLanding />} />
                <Route path="/l/:slug/thanks" element={<PublicThanks />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
              <Routes>
                <Route path="/" element={<MarketingHome />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route path="/aide" element={<HelpCenter />} />
                <Route path="/confidentialite" element={<PrivacyPolicy />} />
                <Route path="/l/:slug" element={<PublicLanding />} />
                <Route path="/l/:slug/thanks" element={<PublicThanks />} />

                <Route path="/app" element={<AppLayout />}>
                  <Route index element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="leads" element={<Leads />} />
                  <Route path="landings" element={<Landings />} />
                  <Route path="landings/new" element={<LandingEditor />} />
                  <Route path="landings/:id/edit" element={<LandingEditor />} />
                  <Route path="landings/:id/stats" element={<LandingStats />} />
                  <Route path="utm-generator" element={<UtmGenerator />} />
                  <Route path="templates" element={<Templates />} />
                  <Route path="templates/new" element={<TemplateEditor />} />
                  <Route path="templates/:id/edit" element={<TemplateEditor />} />
                  <Route path="integrations" element={<Integrations />} />
                  <Route path="settings" element={<AppSettings />} />
                  <Route path="account" element={<Account />} />
                  <Route path="brand" element={<Brand />} />
                  <Route path="linkinbio" element={<LinkInBio />} />
                  <Route path="admin" element={<Admin />} />
                  <Route path="seo" element={<AdminSEO />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            )}
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
